import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Package, IndianRupee, Loader2, History, ArrowRight, ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";

const FreightEstimator = ({ user, onLogout, Layout, isAdmin = false }) => {
  const [sellers, setSellers] = useState([]);
  const [sourcePin, setSourcePin] = useState("");
  const [consigneePin, setConsigneePin] = useState("");
  const [weightKg, setWeightKg] = useState("");
  const [dimensions, setDimensions] = useState([{ length_cm: "", width_cm: "", height_cm: "", box_count: "1" }]);
  const [paymentMode, setPaymentMode] = useState("prepaid");
  const [codAmount, setCodAmount] = useState("");
  const [freightMode, setFreightMode] = useState("fod");
  const [rovInsurance, setRovInsurance] = useState("true");
  const [invAmount, setInvAmount] = useState("");
  const [selectedSeller, setSelectedSeller] = useState("");
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [showSellerBreakdown, setShowSellerBreakdown] = useState(false);

  const updateDimension = (index, field, value) => {
    setDimensions(prev => prev.map((d, i) => i === index ? { ...d, [field]: value } : d));
  };
  const addDimension = () => {
    setDimensions(prev => [...prev, { length_cm: "", width_cm: "", height_cm: "", box_count: "1" }]);
  };
  const removeDimension = (index) => {
    if (dimensions.length <= 1) return;
    setDimensions(prev => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (isAdmin) fetchSellers();
    fetchHistory();
  }, []);

  const fetchSellers = async () => {
    try {
      const res = await axios.get(`${API}/sellers-org/`);
      setSellers(res.data);
    } catch (e) {
      console.error("Failed to fetch sellers", e);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/tat/freight/history?limit=20`);
      setHistory(res.data);
    } catch (e) {
      console.error("Failed to fetch freight history", e);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleEstimate = async () => {
    if (!sourcePin || sourcePin.length !== 6 || !consigneePin || consigneePin.length !== 6) {
      toast.error("Enter valid 6-digit pincodes"); return;
    }
    if (!weightKg || parseFloat(weightKg) <= 0) {
      toast.error("Enter a valid weight"); return;
    }
    if (!invAmount || parseFloat(invAmount) <= 0) {
      toast.error("Enter invoice amount"); return;
    }

    setCalculating(true);
    setResult(null);
    try {
      const body = {
        dimensions: dimensions.map(d => ({
          length_cm: parseFloat(d.length_cm) || 10,
          width_cm: parseFloat(d.width_cm) || 10,
          height_cm: parseFloat(d.height_cm) || 10,
          box_count: parseInt(d.box_count) || 1
        })),
        weight_g: parseFloat(weightKg) * 1000,
        source_pin: sourcePin,
        consignee_pin: consigneePin,
        payment_mode: paymentMode,
        cod_amount: paymentMode === "cod" ? parseFloat(codAmount) || 0 : 0,
        inv_amount: parseFloat(invAmount) || 0,
        freight_mode: freightMode,
        rov_insurance: rovInsurance === "true",
        seller_id: isAdmin ? selectedSeller : null
      };
      const res = await axios.post(`${API}/tat/freight/estimate`, body);
      setResult(res.data);
      fetchHistory();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Freight estimation failed");
    } finally {
      setCalculating(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2">Freight Estimator</h1>
          <p className="text-slate-600">Estimate shipping charges{isAdmin ? " — Charged by Delhivery + Charged to Seller" : ""}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" style={{ color: '#00A699' }} />
                  Shipment Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Pincodes */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Source Pincode</Label>
                    <Input maxLength={6} placeholder="e.g. 400607" value={sourcePin} onChange={(e) => setSourcePin(e.target.value.replace(/\D/g, ""))} data-testid="freight-source-pin" />
                  </div>
                  <div>
                    <Label>Consignee Pincode</Label>
                    <Input maxLength={6} placeholder="e.g. 400604" value={consigneePin} onChange={(e) => setConsigneePin(e.target.value.replace(/\D/g, ""))} data-testid="freight-dest-pin" />
                  </div>
                </div>
                {/* Weight */}
                <div className="w-48">
                  <Label>Total Weight (kg)</Label>
                  <Input type="number" step="0.1" placeholder="e.g. 10" value={weightKg} onChange={(e) => setWeightKg(e.target.value)} data-testid="freight-weight" />
                </div>
                {/* Dimensions array */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Box Dimensions</Label>
                    <button onClick={addDimension} className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700">
                      <Plus className="w-3.5 h-3.5" /> Add Box Type
                    </button>
                  </div>
                  <div className="space-y-2">
                    {dimensions.map((dim, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="grid grid-cols-4 gap-2 flex-1">
                          <Input type="number" placeholder="L (cm)" value={dim.length_cm} onChange={(e) => updateDimension(i, "length_cm", e.target.value)} />
                          <Input type="number" placeholder="W (cm)" value={dim.width_cm} onChange={(e) => updateDimension(i, "width_cm", e.target.value)} />
                          <Input type="number" placeholder="H (cm)" value={dim.height_cm} onChange={(e) => updateDimension(i, "height_cm", e.target.value)} />
                          <Input type="number" min="1" placeholder="Qty" value={dim.box_count} onChange={(e) => updateDimension(i, "box_count", e.target.value)} />
                        </div>
                        {dimensions.length > 1 && (
                          <button onClick={() => removeDimension(i)} className="p-1.5 text-slate-400 hover:text-red-500 rounded transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <p className="text-xs text-slate-400">Length × Width × Height (cm) × Box Count per type</p>
                  </div>
                </div>
                {/* Payment & Freight mode */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label>Invoice Amount (₹)</Label>
                    <Input type="number" placeholder="e.g. 5000" value={invAmount} onChange={(e) => setInvAmount(e.target.value)} data-testid="freight-inv-amount" />
                  </div>
                  <div>
                    <Label>Payment Mode</Label>
                    <Select value={paymentMode} onValueChange={setPaymentMode}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="prepaid">Prepaid</SelectItem>
                        <SelectItem value="cod">COD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Freight Mode</Label>
                    <Select value={freightMode} onValueChange={setFreightMode}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fod">FOD (Freight on Delivery)</SelectItem>
                        <SelectItem value="fop">FOP (Freight on Pickup)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>ROV Insurance</Label>
                    <Select value={rovInsurance} onValueChange={setRovInsurance}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Carrier Risk</SelectItem>
                        <SelectItem value="false">Owner Risk</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {paymentMode === "cod" && (
                  <div className="w-1/2">
                    <Label>COD Amount (₹)</Label>
                    <Input type="number" placeholder="COD amount" value={codAmount} onChange={(e) => setCodAmount(e.target.value)} />
                  </div>
                )}
                {/* Seller selection (admin only) */}
                {isAdmin && sellers.length > 0 && (
                  <div>
                    <Label>Seller (for markup calculation)</Label>
                    <Select value={selectedSeller} onValueChange={setSelectedSeller}>
                      <SelectTrigger data-testid="freight-seller-select"><SelectValue placeholder="Select Seller" /></SelectTrigger>
                      <SelectContent>
                        {sellers.map(s => (
                          <SelectItem key={s.seller_id} value={s.seller_id}>{s.brand_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button
                  onClick={handleEstimate}
                  disabled={calculating || sourcePin.length !== 6 || consigneePin.length !== 6 || !weightKg || !invAmount}
                  className="w-full" style={{ backgroundColor: '#00A699' }}
                  data-testid="freight-calculate-btn"
                >
                  {calculating ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Calculating...</>) : (<><IndianRupee className="w-4 h-4 mr-2" />Estimate Freight</>)}
                </Button>
              </CardContent>
            </Card>

            {/* Results */}
            {result && (
              <div className="space-y-6">
                {/* Charged by Delhivery - admin only */}
                {isAdmin && result.delhivery && (
                  <Card className="border-0 shadow-lg border-l-4 border-l-blue-500" data-testid="freight-delhivery-result">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-blue-700">Charged by Delhivery</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-slate-600 text-sm">Charged Weight: {result.delhivery.charged_wt} kg</span>
                        <span className="text-2xl font-bold text-blue-700">₹{result.delhivery.total?.toLocaleString()}</span>
                      </div>
                      <button onClick={() => setShowBreakdown(!showBreakdown)} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700">
                        {showBreakdown ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        {showBreakdown ? "Hide" : "View"} Breakup
                      </button>
                      {showBreakdown && result.delhivery.price_breakup && (
                        <div className="mt-2 bg-slate-50 rounded-lg p-4 text-sm space-y-1">
                          {Object.entries(result.delhivery.price_breakup).filter(([k, v]) => k !== "meta_charges" && k !== "oda" && typeof v === "number" && v > 0).map(([k, v]) => (
                            <div key={k} className="flex justify-between"><span className="text-slate-500">{k.replace(/_/g, " ")}</span><span className="font-medium">₹{v.toLocaleString()}</span></div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
                {/* Charged to Seller */}
                {result.seller_charges && (
                  <Card className="border-0 shadow-lg border-l-4 border-l-teal-500" data-testid="freight-seller-result">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-teal-700">Charged to Seller{result.seller_name ? ` (${result.seller_name})` : ""}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-slate-600 text-sm">Charged Weight: {result.seller_charges.charged_wt_used} kg</span>
                        <span className="text-2xl font-bold text-teal-700">₹{result.seller_charges.final_amount?.toLocaleString()}</span>
                      </div>
                      <button onClick={() => setShowSellerBreakdown(!showSellerBreakdown)} className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700">
                        {showSellerBreakdown ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        {showSellerBreakdown ? "Hide" : "View"} Breakup
                      </button>
                      {showSellerBreakdown && (
                        <div className="mt-2 bg-slate-50 rounded-lg p-4 text-sm space-y-1">
                          {/* Forward charges breakdown */}
                          {result.seller_charges.fwd_charges?.breakdown && Object.entries(result.seller_charges.fwd_charges.breakdown).map(([k, v]) => {
                            const val = v?.seller_value;
                            if (val === undefined || val === null || val === 0) return null;
                            return (
                              <div key={k} className="flex justify-between">
                                <span className="text-slate-500">{k.replace(/_/g, " ")}</span>
                                <span className="font-medium">₹{typeof val === 'number' ? val.toLocaleString() : val}</span>
                              </div>
                            );
                          })}
                          {/* Pre-tax custom charges */}
                          {result.seller_charges.fwd_charges?.pre_tax_custom_charges?.map((c, i) => (
                            <div key={`pre-${i}`} className="flex justify-between">
                              <span className="text-slate-500">{c.name?.replace(/_/g, " ")}</span>
                              <span className="font-medium">₹{c.amount?.toLocaleString()}</span>
                            </div>
                          ))}
                          {/* Pre-tax subtotal */}
                          {result.seller_charges.fwd_charges?.pre_tax_freight > 0 && (
                            <div className="flex justify-between border-t border-slate-200 pt-1 mt-1">
                              <span className="text-slate-500">Pre-tax Freight</span>
                              <span className="font-medium">₹{result.seller_charges.fwd_charges.pre_tax_freight?.toLocaleString()}</span>
                            </div>
                          )}
                          {/* GST */}
                          {(result.seller_charges.fwd_charges?.gst > 0 || result.seller_charges.fwd_charges?.gst_amount > 0) && (
                            <div className="flex justify-between">
                              <span className="text-slate-500">GST ({result.seller_charges.fwd_charges?.gst_percent || 18}%)</span>
                              <span className="font-medium">₹{(result.seller_charges.fwd_charges.gst || result.seller_charges.fwd_charges.gst_amount)?.toLocaleString()}</span>
                            </div>
                          )}
                          {/* Post-tax custom charges */}
                          {result.seller_charges.fwd_charges?.post_tax_custom_charges?.map((c, i) => (
                            <div key={`post-${i}`} className="flex justify-between">
                              <span className="text-slate-500">{c.name?.replace(/_/g, " ")} (post-tax)</span>
                              <span className="font-medium">₹{c.amount?.toLocaleString()}</span>
                            </div>
                          ))}
                          <div className="flex justify-between border-t border-slate-200 pt-1 mt-1">
                            <span className="text-slate-600 font-medium">Fwd Charges Total</span>
                            <span className="font-semibold">₹{result.seller_charges.fwd_charges?.total?.toLocaleString()}</span>
                          </div>
                          {result.seller_charges.rto_charges?.total > 0 && (
                            <div className="flex justify-between">
                              <span className="text-slate-500">RTO Charges</span>
                              <span className="font-medium">₹{result.seller_charges.rto_charges.total?.toLocaleString()}</span>
                            </div>
                          )}
                          {result.seller_charges.management_fee?.enabled && (
                            <div className="flex justify-between">
                              <span className="text-slate-500">Management Fee ({result.seller_charges.management_fee.percentage}%)</span>
                              <span className="font-medium">₹{result.seller_charges.management_fee.amount?.toLocaleString()}</span>
                            </div>
                          )}
                          <div className="flex justify-between border-t border-slate-200 pt-1 mt-1">
                            <span className="text-slate-700 font-semibold">Total</span>
                            <span className="font-bold text-teal-700">₹{result.seller_charges.final_amount?.toLocaleString()}</span>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
                {!result.seller_charges && isAdmin && (
                  <p className="text-sm text-amber-600 bg-amber-50 p-4 rounded-lg">No commercial structure found for this seller + shipping partner. Select a seller with configured commercial structure.</p>
                )}
              </div>
            )}
          </div>

          {/* History */}
          <div className="lg:col-span-1">
            <Card className="border-0 shadow-lg">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <History className="w-4 h-4 text-slate-500" />
                  Search History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingHistory ? (
                  <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
                ) : history.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-6">No searches yet</p>
                ) : (
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {history.map((item, i) => (
                      <div key={item.id || i} className="p-4 rounded-lg border border-slate-100 text-sm">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-slate-700">{item.source_pin}</span>
                            <ArrowRight className="w-3 h-3 text-slate-400" />
                            <span className="font-medium text-slate-700">{item.consignee_pin}</span>
                          </div>
                          <span className="text-xs font-medium text-slate-500">{item.weight_g ? `${(item.weight_g/1000).toFixed(1)}kg` : ""}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-slate-400">
                          <span>{item.user_name || ""}{item.seller_name ? ` | ${item.seller_name}` : ""}</span>
                          <div className="flex gap-1">
                            {isAdmin && item.delhivery_total && <Badge className="bg-blue-50 text-blue-600 border-0 text-xs">₹{item.delhivery_total}</Badge>}
                            {item.seller_total && <Badge className="bg-teal-50 text-teal-600 border-0 text-xs">₹{item.seller_total}</Badge>}
                          </div>
                        </div>
                        <div className="text-xs text-slate-400 mt-1">{formatToIST(item.created_at)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default FreightEstimator;
